import * as EmailValidator from 'email-validator'
import {useState} from 'react'
import {v4 as uuidv4} from 'uuid'

import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import {useSignupRedirect} from '@/hooks/use-signup-redirect'
import useSubmit from '@/hooks/use-submit'
import {wait} from '@/utils/wait'
import useNavigate from '@helios/shared/hooks/use-navigate'
import {useLocale} from '@helios/shared/providers/locale'
import {identify, track, TrackingEvent} from '@helios/shared/utils/analytics'

export interface Args {
    redirectPath?: string
    form: string
    trackQualifyingLeads?: boolean
    params?: Record<string, string>
}

export enum ErrorType {
    INVALID_EMAIL = 'INVALID_EMAIL',
    NON_WORK_EMAIL = 'NON_WORK_EMAIL',
}

const useSignup = ({form, trackQualifyingLeads = true, ...props}: Args) => {
    const currentLocale = useLocale()
    const [email, setEmail] = useState('')
    const isCompanyEmail = useIsCompanyEmail(email)
    const isValidEmail = EmailValidator.validate(email)
    const navigate = useNavigate()
    const [touchedForm, setTouchedForm] = useState(false)
    const redirectPath = useSignupRedirect({
        redirectPath: props.redirectPath,
        params: {...props.params, email},
    })

    const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!isValidEmail || !isCompanyEmail) {
            if (currentLocale.isPreLaunch) {
                track(TrackingEvent.PreLaunchEmailSignupActioned, {
                    action: 'blocked',
                    form,
                    email,
                })
            } else {
                track(TrackingEvent.EmailSignupActioned, {
                    action: 'blocked',
                    form,
                    email,
                })
            }
        }

        if (!isValidEmail) {
            throw new Error(ErrorType.INVALID_EMAIL)
        }

        if (!isCompanyEmail) {
            throw new Error(ErrorType.NON_WORK_EMAIL)
        }

        if (currentLocale.isPreLaunch) {
            track(TrackingEvent.PreLaunchEmailSignupActioned, {
                action: 'submitted',
                form,
                email,
            })
        } else {
            track(TrackingEvent.EmailSignupActioned, {
                action: 'submitted',
                form,
                email,
            })
        }

        identify({
            external_id: uuidv4(),
            form,
            email,
        })

        if (trackQualifyingLeads && !email?.match(/.*gmail.*/i)) {
            track(TrackingEvent.SignupActioned, {action: 'started', value: 1})
        }

        /**
         * A short timeout to give the track calls more time to complete
         * before redirecting. Works like Segment's `trackForm` method, but
         * allows us to programmatically invoke the track events.
         *
         * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track-form
         */
        await wait(400)

        if (redirectPath.startsWith('http')) {
            window.location.assign(redirectPath)
        } else {
            navigate(redirectPath)
        }
    }

    const {handleSubmit, loading, error, clearError} = useSubmit(handleSignup)

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // we trigger this event only once
        if (currentLocale.isPreLaunch && !touchedForm) {
            track(TrackingEvent.PreLaunchEmailSignupActioned, {
                action: 'interacted',
                form,
            })
        }
        setTouchedForm(true)
        clearError()
        setEmail(e.target.value)
    }

    return {handleSubmit, handleEmailChange, email, loading, error, clearError, setEmail}
}

export default useSignup
